import { Box, Column, CustomSpinner, Text } from '~/commonComponents';
import { Colors } from '~/themes/colors';
import { CSSProperties, useMemo } from 'react';
import { IDataGridProps } from './Types';
import { Styles } from './Constants';

export function DataGrid<T>({
  EmptyComponent,
  PaginationComponent,
  columns,
  data,
  dataCy,
  dataTestId,
  isLoading,
  isStickyHeader = false,
  getRowStyle,
  keyExtractor,
  onRowClick,
}: IDataGridProps<T>) {
  const gridTemplateColumns = useMemo(
    () => columns.map((col) => col.width || '1fr').join(' '),
    [columns],
  );

  const getCellStyle = (align?: 'left' | 'center' | 'right'): CSSProperties => {
    if (!align || align === 'left') return {};
    return {
      display: 'flex',
      justifyContent: align,
      alignItems: 'center',
    };
  };

  if (!isLoading && data.length === 0) {
    return (
      <Column data-cy={dataCy?.root} data-testid={dataTestId?.root}>
        {EmptyComponent}
      </Column>
    );
  }

  return (
    <Column data-cy={dataCy?.root} data-testid={dataTestId?.root}>
      <Box
        data-cy={dataCy?.header}
        data-testid={dataTestId?.header}
        style={{
          ...Styles.Header,
          ...(isStickyHeader ? Styles.HeaderSticky : {}),
          gridTemplateColumns,
        }}
      >
        {columns.map((column) => (
          <Box key={column.key} style={getCellStyle(column.align)}>
            {typeof column.header === 'string' ? (
              <Text bold color={Colors.primary_500} fontSize={18}>
                {column.header}
              </Text>
            ) : (
              column.header
            )}
          </Box>
        ))}
      </Box>
      {isLoading ? (
        <Box marginTop={24}>
          <CustomSpinner isLoading size={16} />
        </Box>
      ) : (
        <>
          <Column style={Styles.Body}>
            {data.map((item) => (
              <Box
                data-cy={dataCy?.rowItem}
                data-testid={dataTestId?.rowItem}
                key={keyExtractor(item)}
                style={{
                  ...Styles.BodyRowItem,
                  cursor: onRowClick ? 'pointer' : 'default',
                  gridTemplateColumns,
                  ...getRowStyle?.(item),
                }}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => (
                  <Box key={column.key} style={getCellStyle(column.align)}>
                    {column.render(item)}
                  </Box>
                ))}
              </Box>
            ))}
          </Column>
          {PaginationComponent && <Column marginTop={24}>{PaginationComponent}</Column>}
        </>
      )}
    </Column>
  );
}
